/**
 * Тексты отказов входа через сервисы. Один словарь на всё приложение.
 *
 * Живёт отдельным файлом, потому что читают его двое: экран входа и корневой
 * экран. При `google_other_user` сессия остаётся живой и человек видит кабинет,
 * а не форму входа, значит сообщение обязано существовать вне `SignIn`.
 *
 * Тексты по-русски и без техножаргона: слова «state», «токен» и «провайдер»
 * пользователю ничего не объясняют. Каждая строка говорит, что случилось и что
 * делать дальше, и почти каждая напоминает про запасной путь: ученику аккаунт
 * не нужен вовсе.
 */
export function googleErrorText(code: string): string {
  switch (code) {
    case "google_cancelled":
      return "Вход через Google отменён.";
    // Просроченный и потерянный state это для человека одно и то же событие:
    // он ушёл на экран Google и вернулся слишком поздно или не туда
    case "google_state_missing":
    case "google_state_mismatch":
      return "Вход занял слишком много времени. Попробуйте ещё раз.";
    case "google_upstream":
      return "Google сейчас недоступен. Войдите по ссылке от преподавателя или попробуйте позже.";
    case "google_bad_token":
      return "Google вернул ответ, который мы не смогли проверить. Попробуйте ещё раз.";
    case "google_link_required":
      return "Этот аккаунт Google ещё не привязан. Сначала выйдите, либо привяжите его позже: экран привязки появится в следующей версии.";
    case "google_other_user":
      return "Этот аккаунт Google принадлежит другому пользователю. Выйдите и повторите.";
    case "google_account_disabled":
      return "Этот аккаунт удалён.";
    // Пользователь этого входа соединён с другим учеником переносом
    // (`docs/account-adopt-design.md`, 8.4). Пароль или Google верные, просто
    // занятия теперь открываются ссылкой от преподавателя
    case "google_account_merged":
      return GOOGLE_ACCOUNT_MERGED_TEXT;
    // Приглашение преподавателя (раздел 9 docs/admin-panel-design.md). Коды из
    // общего словаря `packages/shared/src/errors.ts`, приезжают тем же `auth_error`
    case "teacher_invite_expired":
      return "Срок приглашения истёк. Попросите новое у того, кто его прислал.";
    case "teacher_invite_revoked":
      return "Это приглашение отменено. Попросите новое у того, кто его прислал.";
    case "teacher_invite_used":
      return "По этому приглашению кабинет уже заведён. Если это были вы, войдите тем же аккаунтом Google.";
    // Вход со страницы приглашения сорвался до проверки приглашения. Кнопка
    // Google на этом экране кода не несёт, поэтому вернуть надо на ссылку
    case "teacher_invite_retry":
      return "Вход не завершился. Откройте ссылку приглашения ещё раз и снова войдите через Google.";
    case "teacher_invite_not_found":
      return "Ссылка приглашения не подошла. Откройте её заново целиком или попросите новую.";
    default:
      return "Войти через Google не получилось. Попробуйте ещё раз.";
  }
}

/**
 * Строка 9б (`adopt_open_elsewhere`): этот браузер вошёл ссылками не всех
 * преподавателей ученика. Путь называется рабочий ЗДЕСЬ, а не «устройство, где
 * он занимается обычно»: такого может не быть вовсе (ноутбук с одной ссылкой,
 * телефон с другой). Имён остальных преподавателей нет намеренно: держателю
 * чужого ключа их знать незачем (14).
 */
export const ELSEWHERE_STEPS_TEXT =
  "Если у вас есть пароль, войдите по нему, а потом откройте эту ссылку ещё раз. Если пароля нет, откройте в этом браузере ссылки от остальных преподавателей ученика, а потом эту ещё раз.";

const ACCOUNT_MERGED_TEXT =
  "Занятия с этим логином теперь открываются по ссылке от преподавателя. Если ссылки нет, попросите её у преподавателя.";
const GOOGLE_ACCOUNT_MERGED_TEXT =
  "Занятия с этим аккаунтом Google теперь открываются по ссылке от преподавателя. Если ссылки нет, попросите её у преподавателя.";

/**
 * Фраза неполной сессии (9.6 `docs/account-adopt-design.md`): этот браузер
 * вошёл ссылками не всех преподавателей ученика, и подпись на доске и пояс
 * здесь не меняются. Показывается по нажатию на неактивный пункт, запроса нет.
 *
 * ⚠️ Наличие пароля неполной сессии не отдаётся (2а.6), поэтому фраза общая:
 * «если он у вас есть». Google не назван: ученик со ссылкой привязать его не
 * может (`google_link_required`), а неполная сессия бывает только у ученика.
 */
export const SESSION_PARTIAL_TEXT =
  "Здесь это не поменять. Поменять можно в браузере, где открыты ссылки от всех ваших преподавателей, или после входа по паролю, если он у вас есть.";

/**
 * Тексты отказов переноса заглушки и того, что он оставляет после себя
 * (`docs/account-adopt-design.md`, 8.4). `null`: код не из этого словаря.
 *
 * Экраны вопроса и отказов на «Да» (`features/join/AdoptConfirm.tsx`) рисуют
 * свои тексты с именами; здесь короткие строки для мест, где имён нет:
 * общий экран «Ссылка не сработала», окна настроек, вход паролем.
 */
export function accountErrorText(code: string): string | null {
  switch (code) {
    case "account_merged":
      return ACCOUNT_MERGED_TEXT;
    case "google_account_merged":
      return GOOGLE_ACCOUNT_MERGED_TEXT;
    case "session_partial":
      return SESSION_PARTIAL_TEXT;
    // Видит только преподаватель: карточка ученика в адресе переехала
    case "student_moved":
      return "Этот ученик теперь в другой карточке. Откройте его из списка учеников.";
    // Четыре кода `adopt_*` ниже экран переноса разбирает сам
    // (`adoptRefusalOf`) и до общего экрана их не пускает: это запасные
    // тексты на случай нового места, где их покажут
    case "adopt_stale":
      return "Пока открывалась ссылка, что-то изменилось. Откройте её ещё раз.";
    case "adopt_in_lesson":
      return "Соединить занятия получится после урока. Откройте ссылку ещё раз.";
    case "adopt_open_elsewhere":
      return ELSEWHERE_STEPS_TEXT;
    case "adopt_needs_support":
      return "Соединить занятия не получилось. Скажите об этом преподавателю.";
    default:
      return null;
  }
}
